/**
 * Wall-focus helper.
 *
 * Drives a `RenderViewState` (typically the floating widget's) into an X-Ray
 * camera locked to a single skeleton edge: perpendicular framing, fits the
 * full wall, scene clipping disabled, replaced with a single oriented box
 * around the wall + margin.
 *
 * The focus lives in a custom view tab on the RenderViewState — see
 * `RenderViewState.createOrActivateCustomView`. Two entry points:
 *  - `focusOnWall(state, spec, preview)` — persistent focus (click-select).
 *    Creates the custom tab as persistent or promotes an existing transient
 *    one. Wipes any inner-snapshot the caller had stashed for hover-restore.
 *  - `previewOnWall(state, spec, preview)` / `clearPreview(state, preview)`
 *    — hover preview. If the tab doesn't yet exist, creates a transient one
 *    that auto-removes on `clearPreview`. If a persistent tab is already
 *    there (the user click-focused, then hovered another wall), `preview`
 *    snapshots the persistent tab's state and `clearPreview` restores it.
 *
 * The math (camera distance from FOV/aspect, deterministic side selection,
 * box rotation) lives here rather than on `RenderViewState` because it's
 * skeleton-tool–specific. Other transient-overlay tools should consume the
 * same generic primitives (`captureTransientState` / `restoreTransientState`
 * / `setCameraPose` / `setClipOverride` / `setDensityModeOverride`) and add
 * their own helper module.
 */

import * as THREE from 'three';
import { CameraMode, ClipTask } from '../../../defines';
import { BoxVolume } from '../../../utils/Volume';
import { CUSTOM_VIEW_ID_PREFIX } from '../../../viewer/RenderViewState';
import type { RenderViewState, TransientStateSnapshot } from '../../../viewer/RenderViewState';
import type { AnnotationCategory } from '../../../viewer/AnnotationBlacklistStack';
import type { ClipBoxData } from '../../../viewer/buildClipState';

/** Stable id for the wall-focus custom view tab (one tab shared across edges). */
export const WALL_FOCUS_VIEW_ID = `${CUSTOM_VIEW_ID_PREFIX}wall-focus`;
/** Raw English label — UI passes through `<T _str={label} />` for translation. */
export const WALL_FOCUS_VIEW_LABEL = 'Wall';

/**
 * Description of a wall to focus on. World-space, Z-up.
 *
 * `start`/`end` are the wall's centerline endpoints; only X/Y are used so a
 * slightly-off endpoint Z doesn't tilt the box. `leftSize`/`rightSize` are
 * the wall thicknesses on either side of the centerline (perpendicular,
 * in meters). `floorZ`/`ceilingZ` define the vertical extent.
 */
export interface WallFocusSpec {
  start: THREE.Vector3;
  end: THREE.Vector3;
  leftSize: number;
  rightSize: number;
  floorZ: number;
  ceilingZ: number;
  /**
   * Group id of the floor this wall belongs to. Scopes the wall-focus filter
   * to openings on this floor; `null` shows openings on any floor.
   */
  floorGroupId: string | null;
  /** Extra padding (meters) around the wall on every axis. Default 0.3. */
  margin?: number;
}

/**
 * Opaque preview handle. Caller owns one of these per (tool, view) pair and
 * passes it to `previewOnWall` + `clearPreview`. `innerSnapshot` is set when
 * a hover begins on top of an already-persistent wall-focus tab — it
 * captures the persistent tab's pre-hover overrides so `clearPreview` can
 * restore them. When no persistent tab existed at hover-start, the snapshot
 * stays null and `clearPreview` removes the transient tab instead (which
 * snaps back to the tab's `origin` — i.e. the global view the user was on).
 */
export interface WallFocusPreview {
  innerSnapshot: TransientStateSnapshot | null;
}

export function createWallFocusPreview(): WallFocusPreview {
  return { innerSnapshot: null };
}

/**
 * Persistent focus — call from click-select. Creates the wall-focus tab as
 * persistent (or promotes an existing transient one), then frames the wall.
 * Discards any inner-snapshot the preview handle was holding: the click is a
 * commitment, the hover-restore-target is no longer relevant.
 */
export function focusOnWall(state: RenderViewState, spec: WallFocusSpec, preview?: WallFocusPreview, origin?: TransientStateSnapshot): void {
  if (preview) preview.innerSnapshot = null;
  // `origin` lets the caller pin the tab's snap-back state to a moment BEFORE
  // it mutated the view (the main-view path forces a global X-Ray switch
  // before calling this). Without it, exit would restore the post-switch
  // X-Ray pose instead of the user's true pre-focus camera/rotation.
  state.createOrActivateCustomView({ id: WALL_FOCUS_VIEW_ID, label: WALL_FOCUS_VIEW_LABEL, persistent: true, origin });
  applyWallFocus(state, spec);
}

/**
 * 3D-perspective variant of `focusOnWall`. Same clip box (same wall slab is
 * visible), but the camera is perspective and offset back from the wall so
 * the user gets a 3D feel rather than a flat elevation. Use this in the
 * widget when the main view already owns the orthographic wall-focus card —
 * the widget then renders the same slab from a 3D angle.
 */
export function focusOnWall3D(state: RenderViewState, spec: WallFocusSpec, preview?: WallFocusPreview): void {
  if (preview) preview.innerSnapshot = null;
  state.createOrActivateCustomView({ id: WALL_FOCUS_VIEW_ID, label: WALL_FOCUS_VIEW_LABEL, persistent: true });
  applyWallFocus(state, spec, /* perspective */ true);
}

/**
 * Hover preview. First call:
 *  - If no wall-focus tab exists, creates a transient one. `clearPreview`
 *    will later remove it (snapping back to the tab's origin = pre-hover).
 *  - If a persistent tab already exists (click + hover another edge),
 *    snapshots the persistent tab's overrides into `preview.innerSnapshot`
 *    so `clearPreview` can restore them.
 *
 * Subsequent calls (different edges during the same hover session) just
 * re-frame on the new wall — the snapshot/tab decision was already made.
 */
export function previewOnWall(state: RenderViewState, spec: WallFocusSpec, preview: WallFocusPreview): void {
  const existing = state.getCustomView(WALL_FOCUS_VIEW_ID);
  if (!existing) {
    state.createOrActivateCustomView({ id: WALL_FOCUS_VIEW_ID, label: WALL_FOCUS_VIEW_LABEL, persistent: false });
  } else if (existing.persistent && !preview.innerSnapshot) {
    // Hover started on top of a persistent focus. Snapshot the persistent
    // overrides so unhover can restore them. Activate the tab in case the
    // user had switched away to a global view; that's not a typical flow
    // but keeps state consistent.
    preview.innerSnapshot = state.captureTransientState();
    state.createOrActivateCustomView({ id: WALL_FOCUS_VIEW_ID, label: WALL_FOCUS_VIEW_LABEL, persistent: true });
  }
  applyWallFocus(state, spec);
}

/**
 * End-of-hover. Two paths:
 *  - `innerSnapshot` set → restore the persistent tab's pre-hover overrides.
 *  - Otherwise → if the tab is still transient, remove it (which snaps back
 *    to its origin via `RenderViewState.removeCustomView`). If a click
 *    promoted it persistent during the hover, leave it alone.
 */
export function clearPreview(state: RenderViewState, preview: WallFocusPreview): void {
  const snap = preview.innerSnapshot;
  if (snap) {
    preview.innerSnapshot = null;
    state.restoreTransientState(snap);
    return;
  }
  const existing = state.getCustomView(WALL_FOCUS_VIEW_ID);
  if (existing && !existing.persistent) {
    state.removeCustomView(WALL_FOCUS_VIEW_ID);
  }
}

function applyWallFocus(state: RenderViewState, spec: WallFocusSpec, perspective = false): void {
  const margin = spec.margin ?? 0.3;

  // Wall geometry.
  const ax = spec.start.x;
  const ay = spec.start.y;
  const bx = spec.end.x;
  const by = spec.end.y;
  const dx = bx - ax;
  const dy = by - ay;
  const wallLen = Math.hypot(dx, dy);
  if (wallLen < 1e-6) return;

  const ux = dx / wallLen;
  const uy = dy / wallLen;
  // Perpendicular in XY (Z-up). Deterministic side: prefer +X-component;
  // tie-break on +Y. The user picked deterministic over "front of room" so
  // hovering the same edge twice always frames it identically.
  let nx = -uy;
  let ny = ux;
  if (nx < 0 || (nx === 0 && ny < 0)) {
    nx = -nx;
    ny = -ny;
  }

  const wallHeight = Math.max(1e-3, spec.ceilingZ - spec.floorZ);
  const midZ = (spec.floorZ + spec.ceilingZ) / 2;
  const midX = (ax + bx) / 2;
  const midY = (ay + by) / 2;
  const wallThickness = Math.max(1e-3, spec.leftSize + spec.rightSize);

  // Density-mode + EDL overrides. Orthographic (main-view): density ON +
  // EDL OFF — that's the X-Ray look. Perspective (widget): density OFF +
  // EDL ON — that's the regular 3D RGB shader with EDL contour shading.
  // Both flags must be explicitly set (not undefined): the point cloud
  // material is shared, so the main view's X-Ray pass leaves
  // `densityMode = true, useEDL = false` on the material between renders,
  // and the widget would inherit those without an explicit shadow.
  state.setDensityModeOverride(perspective ? false : true);
  state.setEdlOverride(perspective ? true : false);

  // Orthographic so the wall reads as a flat elevation, the way an
  // architectural wall view should — perspective foreshortens edges that
  // are oblique to the wall normal. The orbit controller's ortho path
  // sizes top/bottom from `view.radius` (top = +radius, bottom = -radius);
  // left/right are derived from canvas aspect by `renderOneView` each
  // frame. So choosing radius = larger-of(half-height, half-width / aspect)
  // is what fits the wall without crop.
  const { width, height } = state.getCanvasSize();
  const aspect = width / height;
  const halfHeight = wallHeight / 2 + margin;
  const halfWidth = wallLen / 2 + margin;
  const radius = Math.max(halfHeight, halfWidth / Math.max(1e-3, aspect)) * 1.05;
  // For ortho the camera distance doesn't affect framing, but we still need
  // a positive standoff so the camera isn't sitting inside the box. Use the
  // wall length as a safe back-off — guaranteed larger than the wall thickness.
  // For perspective we back off enough that the wall fits the vertical FOV
  // (assume ~60° default), nudged slightly so the user gets some sense of
  // depth rather than a flat-on view.
  const orthoDistance = wallLen + margin * 2;
  const perspectiveDistance = (halfHeight * 1.05) / Math.tan((60 * Math.PI / 180) / 2);
  const distance = perspective ? perspectiveDistance : orthoDistance;

  const camPos = new THREE.Vector3(midX + nx * distance, midY + ny * distance, midZ);
  const target = new THREE.Vector3(midX, midY, midZ);
  // Roll 0: a wall elevation is upright by definition. Without the explicit
  // roll, the widget controller preserves whatever roll was on the view —
  // e.g. the top-down floor rotation — and the wall renders tilted by it.
  state.setCameraPose(camPos, target, perspective ? CameraMode.PERSPECTIVE : CameraMode.ORTHOGRAPHIC, 0);
  state.setOrbitRadius(radius);

  // Tighten the camera's near/far around the wall slab — only for the
  // orthographic variant. The flat-elevation view wants to clip annotations
  // / helpers / other walls in front of and behind the slab so the wall
  // reads as a clean elevation. The perspective variant (widget) wants the
  // user to keep depth perception; the wall-slab clip box already isolates
  // the point cloud spatially, so leave the projection unconstrained.
  if (!perspective) {
    const halfThickness = wallThickness / 2 + margin;
    state.setCameraClipOverride({
      near: distance - halfThickness,
      far: distance + halfThickness,
    });
  } else {
    state.setCameraClipOverride(null);
  }

  // Build the wall's clip box. Local axes: +x along the wall, +y across
  // (thickness), +z vertical. Z-axis rotation = atan2 of the wall direction.
  const yaw = Math.atan2(uy, ux);
  const box = new BoxVolume({
    position: new THREE.Vector3(midX, midY, midZ),
    scale: new THREE.Vector3(wallLen + margin * 2, wallThickness + margin * 2, wallHeight + margin * 2),
    rotation: new THREE.Vector3(0, 0, yaw),
    clip: true,
  });
  box.assignedClipTask = ClipTask.SHOW_INSIDE;
  box.actualClipTask = ClipTask.SHOW_INSIDE;
  box.updateMatrixWorld(true);

  const clipBoxData: ClipBoxData = {
    box,
    inverse: box.matrixWorld.clone().invert(),
    position: box.getWorldPosition(new THREE.Vector3()),
  };

  state.setClipOverride({
    mode: 'replace',
    state: {
      clipBoxes: [clipBoxData],
      clipPolygons: [],
      clipPlanes: [],
      clusteredSegmentsByPointCloud: new Map(),
    },
  });

  // Whitelist openings on this wall's floor so the elevation reads as the
  // wall + its own doors/windows (shown iff category OPENING AND on this
  // floor). The filter round-trips through the custom view's origin snapshot
  // — removing the tab restores it. A null floorGroupId drops the floor
  // facet, leaving every floor's openings visible.
  state.setAnnotationBlacklist({
    mode: 'whitelist',
    types: new Set<AnnotationCategory>(['OPENING']),
    identifiers: spec.floorGroupId ? new Set([spec.floorGroupId]) : undefined,
  });
}

